import { useState, useCallback, useMemo } from 'react'

interface Device {
  id: string
  name: string
  type: 'mac-mini' | 'physical-phone'
}

interface UseDeviceSelectionReturn {
  selectedDeviceIds: Set<string>
  selectedDevices: Device[]
  isSelected: (deviceId: string) => boolean
  selectDevice: (device: Device) => void
  deselectDevice: (deviceId: string) => void
  toggleDevice: (device: Device) => void
  selectAll: (devices: Device[]) => void
  selectNone: () => void
  selectedCount: number
  hasSelection: boolean
}

export const useDeviceSelection = (
  availableDevices: Device[] = []
): UseDeviceSelectionReturn => {
  const [selectedDeviceIds, setSelectedDeviceIds] = useState<Set<string>>(
    new Set()
  )

  const selectedDevices = useMemo(
    () => availableDevices.filter((device) => selectedDeviceIds.has(device.id)),
    [availableDevices, selectedDeviceIds]
  )

  const isSelected = useCallback(
    (deviceId: string): boolean => selectedDeviceIds.has(deviceId),
    [selectedDeviceIds]
  )

  const selectDevice = useCallback((device: Device) => {
    setSelectedDeviceIds((prev) => new Set(prev).add(device.id))
  }, [])

  const deselectDevice = useCallback((deviceId: string) => {
    setSelectedDeviceIds((prev) => {
      const newSet = new Set(prev)
      newSet.delete(deviceId)
      return newSet
    })
  }, [])

  const toggleDevice = useCallback(
    (device: Device) => {
      if (selectedDeviceIds.has(device.id)) {
        deselectDevice(device.id)
      } else {
        selectDevice(device)
      }
    },
    [selectedDeviceIds, selectDevice, deselectDevice]
  )

  const selectAll = useCallback((devices: Device[]) => {
    setSelectedDeviceIds(new Set(devices.map((device) => device.id)))
  }, [])

  const selectNone = useCallback(() => {
    setSelectedDeviceIds(new Set())
  }, [])

  const selectedCount = selectedDeviceIds.size
  const hasSelection = selectedCount > 0

  return {
    selectedDeviceIds,
    selectedDevices,
    isSelected,
    selectDevice,
    deselectDevice,
    toggleDevice,
    selectAll,
    selectNone,
    selectedCount,
    hasSelection,
  }
}
